import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { mountRootParcel } from 'single-spa';
import type { CustomProps } from 'single-spa';
import { SingleSpaProps } from '../../../single-spa/single-spa-props';
import { BannerTransversalLoaderService } from '../../services/banner-transversal-loader.service';
import { MenuBffService } from '../../services/menubff-service';
import { environment } from '../../../environments/environment';
import type {
  BannerParcelAllowedCustomProps,
  ParcelObject
} from '../../interfaces/single-spa-parcel.interface';
import { ShellIframeService } from '../../services/shell-iframe.service';
import {
  detectBrowser,
  getBrowserReportDetails,
  isBannerBrowserCompatible
} from '../../utils/browser-compatibility.util';
import type { IncompatibleBrowserRequest } from '../../interfaces/incompatible-browser.interface';

/** IDs a inyectar al montar el parcel banner-transversal. 66726131 - Convenios. */
const BANNER_IDS: string[] = ['69705772dd1451ad3c4722d5', '66726131Convenios'];

@Component({
  selector: 'app-banner-transversal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './banner-transversal.component.html',
  styleUrl: './banner-transversal.component.scss'
})
export class BannerTransversalComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() microfrontendProps: SingleSpaProps = {};

  @ViewChild('bannerHost', { static: false })
  bannerHost?: ElementRef<HTMLElement>;

  private parcel: ParcelObject | null = null;

  constructor(
    private readonly loader: BannerTransversalLoaderService,
    private readonly shellIframeService: ShellIframeService,
    private readonly menuBffService: MenuBffService,
    private readonly ngZone: NgZone
  ) {}

  ngAfterViewInit(): void {
    void this.tryMount();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['microfrontendProps'] || !this.bannerHost) {
      return;
    }

    const prevProps = changes['microfrontendProps'].previousValue as SingleSpaProps | undefined;
    const urlSitio = this.getStringProp(this.microfrontendProps, 'UrlSitio');
    const prevUrlSitio = this.getStringProp(prevProps ?? {}, 'UrlSitio');

    if (
      urlSitio === prevUrlSitio &&
      this.microfrontendProps.whitelabel === prevProps?.whitelabel &&
      this.microfrontendProps.Dominio === prevProps?.Dominio &&
      JSON.stringify(this.microfrontendProps.BannerIds) === JSON.stringify(prevProps?.BannerIds) &&
      this.microfrontendProps.SecuenciaNavegacionMenu === prevProps?.SecuenciaNavegacionMenu
    ) {
      return;
    }

    void this.remount();
  }

  ngOnDestroy(): void {
    void this.parcel?.unmount().catch(() => undefined);
    this.parcel = null;
  }

  private async remount(): Promise<void> {
    if (this.parcel) {
      // 1. Obtenemos el estado actual del parcel de Single-SPA
      const status = this.parcel.getStatus?.();
  
      // 2. Si el parcel está a la mitad de un proceso, esperamos a que termine
      if (status === 'LOADING_SOURCE_CODE' || status === 'MOUNTING') {
        try {
          await this.parcel.mountPromise;
        } catch (e) {
          // Ignoramos el error aquí, ya que el catch de tryMount() lo manejó
        }
      }
  
      // 3. Volvemos a verificar el estado. Si se montó con éxito, lo desmontamos de forma segura.
      if (this.parcel.getStatus?.() === 'MOUNTED') {
        await this.parcel.unmount();
      }
      
      // Limpiamos la referencia
      this.parcel = null;
    }
  
    // 4. Volvemos a montar con los nuevos cambios
    await this.tryMount();
  }

  private async checkMicrofrontendAvailability(url: string): Promise<boolean> {
    try {
      // Usamos 'HEAD' para solo verificar que existe sin descargar todo el archivo
      const response = await fetch(url, { method: 'HEAD' });
      return response.ok; // Retorna true si el estado es 200-299
    } catch (error) {
      // Si la conexión es rechazada (ERR_CONNECTION_REFUSED) o el servidor está caído, cae aquí
      return false;
    }
  }

  private async tryMount(): Promise<void> {
    const url = this.resolveBannerUrl(this.microfrontendProps.Dominio);
    if (!environment.bannerTransversalEnabled || !url) {
      return;
    }
  
    if (this.parcel) {
      return;
    }
  
    const domElement = this.bannerHost?.nativeElement;
    if (!domElement) {
      return;
    }
  
    if (this.microfrontendProps.BannerIds?.length === 0 || this.microfrontendProps.BannerIds === undefined) {
      console.warn(`[Banner MF] No se ha configurado ningún banner. Se aborta el montaje para evitar errores en consola.`);
      return;
    }

    // --- NUEVA VALIDACIÓN AQUÍ ---
    const isAvailable = await this.checkMicrofrontendAvailability(url);
    if (!isAvailable) {
      console.warn(`[Banner MF] El microfrontend en ${url} no está disponible. Se aborta el montaje para evitar errores en consola.`);
      return; // Salimos de la función sin intentar inyectarlo
    }
    // -----------------------------

    if (!isBannerBrowserCompatible()) {
      const { browser, version } = detectBrowser(navigator.userAgent);
      console.warn(
        `[Banner MF] Navegador no compatible (${browser}${version ? ` ${version}` : ''}). ` +
          'Se requiere Chrome 93+, Safari 16.3+, Firefox 92+ o Edge 93+. Se aborta el montaje.'
      );
      this.reportIncompatibleBrowser();
      return;
    }

    const bannerProps = this.pickWhitelistedProps(this.microfrontendProps);
   

    const customProps: CustomProps & BannerParcelAllowedCustomProps & { domElement: HTMLElement } = {
      domElement,
      ...bannerProps
    };
    const parcelLoader = (() =>
      this.loader.loadBannerModule(url)) as Parameters<typeof mountRootParcel>[0];
  
    try {
      const mp = this.microfrontendProps.mountParcel;
      const mounted: ParcelObject =
        typeof mp === 'function'
          ? (mp(parcelLoader, customProps) as ParcelObject)
          : (mountRootParcel(parcelLoader, customProps) as ParcelObject);
  
      this.parcel = mounted;
      await mounted.mountPromise;
    } catch (err: unknown) {
      const correlationId = `${Date.now().toString(36)}-mount`;
      const message = err instanceof Error ? err.message : 'mount_failed';
      console.warn(
        JSON.stringify({
          event: 'bannerTransversalMountFailed',
          correlationId,
          timestamp: new Date().toISOString(),
          message
        })
      );
      this.parcel = null;
    }
  }

  private reportIncompatibleBrowser(): void {
    const reportDetails = getBrowserReportDetails(navigator.userAgent);
    const bannerConfigurationIds = this.getBannerConfigurationIds();

    if (!reportDetails || bannerConfigurationIds.length === 0) {
      return;
    }

    const basePayload: Omit<IncompatibleBrowserRequest, 'bannerConfigurationId'> = {
      browser: reportDetails.browser,
      version: reportDetails.version,
      userName: this.getStringProp(this.microfrontendProps, 'userName') ?? this.microfrontendProps.usuario ?? null,
      userEmail: this.getStringProp(this.microfrontendProps, 'userEmail') ?? null,
      companyName: this.getStringProp(this.microfrontendProps, 'companyName') ?? null,
      userDateUtc: null
    };

    for (const bannerConfigurationId of bannerConfigurationIds) {
      this.menuBffService
        .reportarNavegadorIncompatible({
          ...basePayload,
          bannerConfigurationId
        })
        .subscribe({
          error: () => undefined
        });
    }
  }

  private getBannerConfigurationIds(): string[] {
    const bannerIds = this.microfrontendProps.BannerIds ?? BANNER_IDS;

    if (Array.isArray(bannerIds)) {
      return bannerIds
        .filter((id): id is string => typeof id === 'string')
        .map((id) => id.trim())
        .filter((id) => id.length > 0);
    }

    if (typeof bannerIds === 'string' && bannerIds.trim().length > 0) {
      return [bannerIds.trim()];
    }

    return [];
  }

  private pickWhitelistedProps(props: SingleSpaProps): BannerParcelAllowedCustomProps {
    return {
      usuario: props.usuario,
      rol: props.rol,
      whitelabel: props.whitelabel,
      Dominio: props.Dominio,
      IdPais: props.IdPais,
      IdEmpresa: props.IdEmpresa,
      IdUsuario: props.IdUsuario,
      IdOrg: props.IdOrg,
      apiUrl: this.getStringProp(props, 'apiUrl'),
      productId: this.getStringProp(props, 'productId'),
      userId: this.getStringProp(props, 'userId') ?? props.IdUsuario,
      companyId: this.getStringProp(props, 'companyId') ?? this.getNumberAsString(props.IdEmpresa),
      userEmail: this.getStringProp(props, 'userEmail'),
      userName: this.getStringProp(props, 'userName') ?? props.usuario,
      companyName: this.getStringProp(props, 'companyName'),
      UrlSitio: this.getStringProp(props, 'UrlSitio'),
      name: props.name,
      BannerIds: props.BannerIds ?? BANNER_IDS,
      redirectToBanner: (url: string, isRedirect: boolean) => {
        this.ngZone.run(() => {
          this.shellIframeService.solicitarRecargaMenuComprador({ omitirCacheLocal: true });
          if (isRedirect) {
            this.shellIframeService.solicitarRedireccionDesdeBanner(url);
          }
        });
      }
    };
  }

  private resolveBannerUrl(dominio: unknown): string {
    let key: 1 | 2 | 3 = 1;
    if (
      dominio === 2 ||
      dominio === '2' ||
      (typeof dominio === 'string' && dominio.toLowerCase().includes('agilice'))
    ) {
      key = 2;
    } else if (
      dominio === 3 ||
      dominio === '3' ||
      (typeof dominio === 'string' && dominio.toLowerCase().includes('redmat'))
    ) {
      key = 3;
    }
    return environment.urlBannerTransversal[key]?.trim() ?? '';
  }

  private getStringProp(props: SingleSpaProps, key: string): string | undefined {
    const value = props[key];
    return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
  }

  private getNumberAsString(value: number | undefined): string | undefined {
    return typeof value === 'number' && Number.isFinite(value) ? String(value) : undefined;
  }
}
